import React from 'react';

export interface AdminNavBarHeaderProps {
  /**
   * Left side content - typically logo/brand or back button + title
   */
  leftContent: React.ReactNode;
  /**
   * Right side content - typically toggle button or action buttons
   */
  rightContent?: React.ReactNode;
  /**
   * Whether the sidebar is collapsed
   */
  collapsed?: boolean;
  /**
   * Additional className
   */
  className?: string;
}

/**
 * Stateless Admin NavBar Header Component
 * 
 * Shared header component used by both sidebar and page headers.
 * Ensures consistent sizing, styling, and enables smooth animations.
 * 
 * Size: p-4 (16px padding) - matches exactly
 * Height: Auto based on content + padding
 * Border: border-b border-[#504A4A]
 * Background: bg-[#221F1F]
 * 
 * @example
 * ```tsx
 * // Sidebar usage
 * <AdminNavBarHeader
 *   leftContent={
 *     <Link to="/admin/dashboard" className="flex items-center space-x-3 flex-1">
 *       <img src="/rollops-logo.png" alt="RollOps Logo" width={32} height={32} className="w-8 h-8 flex-shrink-0" />
 *       <span className="text-xl font-bold text-[#EBFFEE]">
 *         Roll<span className="text-rollops-accent">O</span>ps
 *       </span>
 *       <span className="text-xs text-[#A9A3A3] ml-2">Backend</span>
 *     </Link>
 *   }
 *   rightContent={
 *     <button onClick={toggle} className="w-8 h-8 flex items-center justify-center...">
 *       <Menu className="w-5 h-5" />
 *     </button>
 *   }
 *   collapsed={!isOpen}
 * />
 * 
 * // Page header usage
 * <AdminNavBarHeader
 *   leftContent={
 *     <div className="flex items-center space-x-3 flex-1">
 *       <Button onClick={goBack}>← Back</Button>
 *       <Building2 className="w-6 h-6 text-rollops-accent" />
 *       <h1 className="text-xl font-bold text-[#EBFFEE]">Company Search</h1>
 *     </div>
 *   }
 *   collapsed={false}
 * />
 * ```
 */
export function AdminNavBarHeader({
  leftContent,
  rightContent,
  collapsed = false,
  className = '',
}: AdminNavBarHeaderProps) {
  // Fixed height to ensure both sidebar and page headers match exactly
  // Height: h-16 (64px) - matches sidebar header exactly
  // Padding: p-4 (16px all around) with flex items-center for vertical centering
  return (
    <div className={`h-16 p-4 border-b border-[#504A4A] bg-[#221F1F] flex items-center ${className}`}>
      {collapsed && !rightContent ? (
        // Collapsed state - center the content if no rightContent
        <div className="flex items-center justify-center w-full">
          {leftContent}
        </div>
      ) : (
        <div className="flex items-center justify-between w-full">
          {leftContent}
          {rightContent && (
            <div className="flex-shrink-0">
              {rightContent}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
